import { Editor, Node, Text, Transforms, Range, createEditor } from 'slate';
import { withReact } from 'nifty-slate-react';
import { jsx } from 'slate-hyperscript';
import { withHistory } from 'slate-history';

import { emojisRegex } from '../md/plugins/emoji';
import { mentionRegex, getMentionById, getMentionBySymbol } from '../md/plugins/mention';

export const NODE_EMOJI = 'emoji';
export const NODE_MENTION = 'mention';

export const isPopulated = node => node.children && node.children.some(n => Node.string(n) || isPopulated(n));

export const serialize = node => {
    if (Text.isText(node)) {
        return node.text;
    }

    const children = node.children ? node.children.map(n => serialize(n)).join('') : '';

    switch (node.type) {
        case 'mention': {
            const mentionType = getMentionById(node.data.type);

            if (mentionType) {
                return `<${mentionType.symbol}${node.data.id}>`;
            }
        }

        case 'emoji':
            return node.data.colons;

        case 'paragraph':
            return `${children}\n`;

        default:
            return children;
    }
}

export const toggleMd = (editor, symbolBefore = '', symbolAfter = '', eachLine) => {
    const countBefore = symbolBefore.length;
    const countAfter = symbolAfter.length;
    const selectedText = getSelectedText(editor, countBefore, countAfter);

    if (!selectedText) {
        return;
    }

    const matchText = testMdText(selectedText.text, countBefore, countAfter, symbolBefore, symbolAfter);
    const matchExtended = testMdText(selectedText.extended, countBefore, countAfter, symbolBefore, symbolAfter);

    if (matchText || matchExtended) {
        const removeOffsetStart = matchText ? selectedText.start : selectedText.start - countBefore;
        const removeOffsetEnd = matchText ? (selectedText.end - countAfter) : selectedText.end;

        Transforms.delete(editor, {
            at: {
                path: selectedText.path,
                offset: removeOffsetEnd
            },
            distance: countAfter,
            unit: 'character'
        });

        Transforms.delete(editor, {
            at: {
                path: selectedText.path,
                offset: removeOffsetStart
            },
            distance: countBefore,
            unit: 'character'
        });
    } else {
        if ('getSelection' in window && window.getSelection().toString().match(/\n$/)) {
            Transforms.move(editor, {
                distance: 2,
                unit: 'offset',
                reverse: true,
                edge: 'end'
            });
        }
        if (eachLine) {
            let start = selectedText.start;
            const end = selectedText.end;
            const text = selectedText.nodeText;

            if (start > 0) {
                if (start - 1 !== 0 && text[start - 1] !== '\n') {
                    while (start !== 0 && text[start - 1] !== '\n') {
                        start -= 1;
                    }
                }
            }

            const lines = text.slice(start, end).split('\n');
            let last = start || 0;

            lines.forEach(line => {
                if (!line.length) {
                    last += 1;
                    return;
                }

                const rangeStart = {
                    path: selectedText.path,
                    offset: last
                };

                const rangeEnd = {
                    path: selectedText.path,
                    offset: last + line.length
                }

                last = last + line.length + countBefore + countAfter + 1;

                wrapAtRange(editor, {
                    start: rangeStart,
                    end: rangeEnd
                }, symbolBefore, symbolAfter)
            })

        } else {
            wrapAtRange(editor, {
                start: {
                    path: selectedText.path,
                    offset: selectedText.start
                },
                end: {
                    path: selectedText.path,
                    offset: selectedText.end
                }
            }, symbolBefore, symbolAfter);

            Transforms.setSelection(editor, {
                anchor: {
                    ...selectedText.originalSelection.anchor,
                    offset: selectedText.originalSelection.anchor.offset + symbolBefore.length
                },
                focus: {
                    ...selectedText.originalSelection.focus,
                    offset: selectedText.originalSelection.focus.offset + symbolBefore.length
                }
            });
        }
    }
}

export const getInputAfterTrigger = (editor, triggerChar, triggerRegex) => {
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection)) {
        const [start] = Range.edges(selection);
        const charBefore = Editor.before(editor, start, { unit: 'character' });
        const charBeforeRange = charBefore && Editor.range(editor, charBefore, start);
        const charBeforeText = charBeforeRange && Editor.string(editor, charBeforeRange);

        if (charBeforeText === triggerChar) {
            return '';
        }

        const wordBefore = Editor.before(editor, start, { unit: 'word' });
        const before = wordBefore && Editor.before(editor, wordBefore);
        const beforeRange = before && Editor.range(editor, before, start);
        const beforeText = beforeRange && Editor.string(editor, beforeRange);
        const beforeMatch = beforeText && beforeText.match(triggerRegex);
        const after = Editor.after(editor, start);
        const afterRange = Editor.range(editor, start, after);
        const afterText = Editor.string(editor, afterRange);
        const afterMatch = afterText.match(/^(\s|$)/);

        if (beforeMatch && afterMatch) {
            return beforeMatch[1];
        }

        return null;
    }
}

export const niftyEditor = () => {
    const editor = withHistory(withReact(createEditor()));
    const { isInline, isVoid, insertData } = editor;

    editor.isInline = element => {
        return [NODE_MENTION, NODE_EMOJI].includes(element.type) ? true : isInline(element);
    }

    editor.isVoid = element => {
        return [NODE_MENTION, NODE_EMOJI].includes(element.type) ? true : isVoid(element);
    }

    editor.insertData = data => {
        const content = data.getData('text/plain');

        if (content) {
            Transforms.insertText(editor, ' ');
            Transforms.move(editor, {
                distance: 1,
                unit: 'offset',
                reverse: true
            });
        }

        insertData(data);
    }

    return editor;
}

export const deserialize = source => {
    try {
        const deserializedJSON = escapeCode(source, escapedSource => {
            const document = new DOMParser().parseFromString(`<p>${convertCustomNodes(escapedSource)}</p>`, 'text/html');
            return JSON.stringify(deserializeElement(document.body));
        });

        const nodes = JSON.parse(deserializedJSON);

        return nodes.map(node => {
            if (node.type) {
                return node;
            };

            return {
                type: 'paragraph',
                children: [
                    {
                        text: node.text
                    }
                ]
            };
        }).filter(node => node.type === 'paragraph' && node.children.length);
    } catch (e) {
        return source;
    }
}

const escapeCode = (source, modifier) => {
    const escCodeMatches = [];
    const codeMatches = [];

    const espacedSource = source
        .replace(/(\\`+)/g, escCode => {
            escCodeMatches.push(escCode);

            return '%NIFTY_ESC_CODE_REPLACER%';
        })
        .replace(/(`+(?=[^]+`)[^]+?`+)/g, code => {
            codeMatches.push(code);

            return '%NIFTY_CODE_REPLACER%';
        });

    return modifier(espacedSource)
        .replace(/%NIFTY_CODE_REPLACER%/g, match => codeMatches.shift())
        .replace(/%NIFTY_ESC_CODE_REPLACER%/g, match => escCodeMatches.shift())
}

export const getNodeText = node => {
    if (Text.isText(node)) {
        return node.text;
    }

    return node.children ? node.children.map(n => getNodeText(n)).join('') : '';
}

const getSelectedText = (editor, extendedByBefore, extendedByAfter) => {
    const selection = editor.selection;

    if (!selection || !selection.anchor || !selection.focus) {
        return null;
    }

    const selectionAnchor = selection.anchor.offset;
    const selectionFocus = selection.focus.offset;
    const start = Math.min(selectionAnchor, selectionFocus);
    let end = Math.max(selectionAnchor, selectionFocus);
    const node = Node.get(editor, selection.anchor);
    const nodeText = getNodeText(node);

    if (nodeText[end - 1] === '\n') {
        end -= 1;
    }

    return {
        node,
        nodeText,
        originalSelection: selection,
        path: selection.anchor.path,
        start,
        end,
        text: nodeText.slice(start, end),
        extended: nodeText.slice(start - extendedByBefore, end + extendedByAfter)
    };
}

const testMdText = (text, countBefore, countAfter, matchBefore, matchAfter) => text.slice(0, countBefore) === matchBefore && text.slice(-countAfter) === matchAfter;

const wrapAtRange = (editor, range, before = '', after = '') => {
    if (after) {
        Transforms.insertText(editor, after, {
            at: range.end
        });
    }

    if (before) {
        Transforms.insertText(editor, before, {
            at: range.start
        });
    }
}

const deserializeElement = el => {
    if (el.nodeType === 3) {
        return el.textContent
    } else if (el.nodeType !== 1) {
        return null;
    }

    const children = Array.from(el.childNodes).map(deserializeElement);

    switch (el.nodeName) {
        case 'BODY':
            return jsx('fragment', {}, children);

        case 'BR':
            return '\n';

        case 'P':
            return jsx('element', { type: 'paragraph' }, children);

        case NODE_MENTION.toUpperCase():
            return jsx(
                'element',
                {
                    type: NODE_MENTION,
                    data: {
                        type: el.getAttribute('type'),
                        id: el.getAttribute('id')
                    }
                },
                [{ text: '' }]
            )

        case NODE_EMOJI.toUpperCase():
            return jsx(
                'element',
                {
                    type: NODE_EMOJI,
                    data: {
                        colons: el.getAttribute('colons')
                    }
                },
                [{ text: '' }]
            )

        default:
            return el.textContent
    }
}

const convertCustomNodes = markdownSource => {
    const markdownUrls = [];

    if (typeof markdownSource !== 'string') {
        return markdownSource;
    }

    let md = markdownSource
        .replace(/([])|\[(.*?)\]\(.*?\)/g, markdownUrl => {
            markdownUrls.push(markdownUrl);

            return '%NIFTY_MARKDOWN_URL_REPLACER%';
        });

    md = md
        .replace(RegExp(mentionRegex, 'g'), (match, symbol, id) => {
            const mentionType = getMentionBySymbol(symbol);
            if (mentionType) {
                return `<${NODE_MENTION} type="${mentionType.id}" id="${id}"></${NODE_MENTION}>`;
            }

            return match;
        });

    md = md.replace(RegExp(emojisRegex, 'g'), match => `<${NODE_EMOJI} colons="${match.trim()}"></${NODE_EMOJI}>`);
    md = md.replace(/%NIFTY_MARKDOWN_URL_REPLACER%/g, match => markdownUrls.shift());

    return md;
}
